import React, { useEffect, useState } from 'react';
import { Slider } from '../ui/slider';
import { Type, Minus, Plus } from 'lucide-react';

const STORAGE_KEY = 'examFontScale';
const MIN = 0.85;
const MAX = 1.4;
const STEP = 0.05;

function useFontScaleWithKey(key) {
  const [scale, setScaleState] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      const n = raw ? parseFloat(raw) : 1;
      return Number.isFinite(n) && n >= MIN && n <= MAX ? n : 1;
    } catch { return 1; }
  });
  const setScale = (n) => {
    const clamped = Math.max(MIN, Math.min(MAX, Number(n) || 1));
    setScaleState(clamped);
    try { localStorage.setItem(key, String(clamped)); } catch {}
  };
  return [scale, setScale];
}

/** React hook: read + persist the exam font scale.
 *  Returns [scale, setScale] where scale is a decimal between MIN and MAX.
 *  Wrap your exam view with `style={{ zoom: scale }}` to apply. */
export function useExamFontScale() {
  return useFontScaleWithKey(STORAGE_KEY);
}

/** Same as useExamFontScale but persisted under a separate key for Game Labs. */
export function useGameLabFontScale() {
  return useFontScaleWithKey('gameLabFontScale');
}

/** Compact slider with A− / A+ steppers. Scales everything inside the
 *  parent that has `style={{ zoom: scale }}` proportionally. */
export function FontSizeSlider({ scale, onChange, className = '' }) {
  const dec = () => onChange(Math.max(MIN, Math.round((scale - STEP) * 100) / 100));
  const inc = () => onChange(Math.min(MAX, Math.round((scale + STEP) * 100) / 100));
  const percent = Math.round(scale * 100);

  return (
    <div
      className={`flex items-center gap-2 px-3 py-1.5 rounded-full border bg-card/60 backdrop-blur ${className}`}
      data-testid="font-size-slider"
      title="Adjust text size — everything scales proportionately"
    >
      <Type className="w-3.5 h-3.5 text-muted-foreground" />
      <button
        type="button"
        onClick={dec}
        aria-label="Smaller text"
        className="w-6 h-6 rounded-full hover:bg-muted flex items-center justify-center"
        data-testid="font-decrease"
        disabled={scale <= MIN}
      >
        <Minus className="w-3.5 h-3.5" />
      </button>
      <div className="w-24">
        <Slider
          min={MIN}
          max={MAX}
          step={STEP}
          value={[scale]}
          onValueChange={(v) => onChange(v[0])}
          data-testid="font-slider"
          aria-label="Text size"
        />
      </div>
      <button
        type="button"
        onClick={inc}
        aria-label="Bigger text"
        className="w-6 h-6 rounded-full hover:bg-muted flex items-center justify-center"
        data-testid="font-increase"
        disabled={scale >= MAX}
      >
        <Plus className="w-3.5 h-3.5" />
      </button>
      <span className="text-xs font-mono tabular-nums text-muted-foreground w-9 text-right">{percent}%</span>
    </div>
  );
}
